import { ApolloQueryResult } from '@apollo/client';
import React, { useState } from 'react';
import { PaymentMethodId, PaymentMethodPanel } from '@atlassian/commerce-payment-methods';
import {
  ErrorComponents,
  FormattedAddress,
  OpenSidebarButton,
  PaymentErrorLozenge,
  SummaryGridItem,
  SummarySection,
  isAccess,
  isResourceAccess,
  useFeatureToggle,
  useSidebar,
  WarningDialog,
} from '@css-xp/base';
import { ManagePaymentMethodsSidebarProps } from '@css-xp/base/common-sidebars';
import { TaxComponent } from '@css-xp/base/tax-exempt/src/tax-component';
import {
  FetchInvoiceGroupV2Query,
  Maybe,
  PaymentMethod,
  PaymentMethodType,
  TermDetailsAccountConnection,
  TransactionAccount,
} from '@css-xp/graphtypes/src/generated-types-new';
import { PaymentMethodRequest } from '@atlassian/cssxp-types-billing';
import { Permissions, Resources } from '@atlassian/cssxp-typescommon';
import { ENABLE_CREDIT_MANAGEMENT, sentenceCase } from '@atlassian/cssxp-utils-generic';
import { SIDEBAR } from '../invoice-group-page';
import './invoice-group-summary.scss';
import Button from '@atlaskit/button';
import { TermPaymentMethod } from '@css-xp/base/term-payment-method';

interface Props {
  invoiceGroup: FetchInvoiceGroupV2Query;
  managePaymentMethodsSidebar: string;
  // eslint-disable-next-line no-unused-vars
  submitAction: (request: PaymentMethodRequest) => Promise<PaymentMethod>;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  isTermPaymentMethodAllowed?: boolean;
}

interface PreparePayloadForSidebarArgs {
  invoiceGroupId: string;
  transactionAccount: TransactionAccount;
  defaultPaymentMethod: Maybe<PaymentMethod>;
  updatePaymentMethodLink: string;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  // eslint-disable-next-line no-unused-vars
  submitAction: (request: PaymentMethodRequest) => Promise<PaymentMethod>;
  isTermPaymentMethodAllowed?: boolean;
  termPaymentStatus?: Maybe<string> | null;
}

const preparePayloadForSidebar = ({
  invoiceGroupId,
  transactionAccount,
  defaultPaymentMethod,
  updatePaymentMethodLink,
  refetch,
  submitAction,
  isTermPaymentMethodAllowed,
  termPaymentStatus,
}: PreparePayloadForSidebarArgs): ManagePaymentMethodsSidebarProps => {
  return {
    invoiceGroupId,
    transactionAccountId: transactionAccount?.id as string,
    updatePaymentMethodLink,
    allPaymentMethods: transactionAccount?.availablePaymentMethods?.nodes,
    defaultPaymentMethodId: defaultPaymentMethod?.id as string,
    defaultPaymentMethodType: defaultPaymentMethod?.type as string,
    refetch,
    submitAction,
    isTermPaymentMethodAllowed,
    termPaymentStatus,
  };
};

export const InvoiceGroupSummary = ({
  invoiceGroup,
  managePaymentMethodsSidebar,
  refetch,
  submitAction,
  isTermPaymentMethodAllowed = true,
}: Props) => {
  const [isEnabled] = useFeatureToggle();
  const { openSidebar } = useSidebar();
  const [isConfirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const isEnableCreditManagement = isEnabled(ENABLE_CREDIT_MANAGEMENT);
  if (!invoiceGroup?.invoiceGroup) {
    return (
      <ErrorComponents.ErrorPanel
        error={new ErrorComponents.NotFoundError(`Unable to find invoice group for page summary.`)}
      />
    );
  }

  const {
    shipToParty: shipToAddress,
    recipients,
    defaultPaymentMethod,
    currency,
    purchaseOrder,
    transactionAccount,
    updatePaymentMethodLink,
    transactionAccountId,
    transactionAccountNumber,
    id,
    termPaymentStatus,
  } = invoiceGroup?.invoiceGroup;
  const defaultPaymentMethodObj = defaultPaymentMethod as any;
  // convert expMonth & expYear to Numbers
  const expMonth = +(defaultPaymentMethodObj?.card?.expirationMonth ?? 0);
  const expYear = +(defaultPaymentMethodObj?.card?.expirationYear ?? 0);
  const isPaymentIssue = defaultPaymentMethodObj?.paymentIssue === true;
  const isRecipientAvailable = recipients && recipients.length > 0;

  let taxExemptFlag;
  if (shipToAddress?.addressLine1) {
    taxExemptFlag = shipToAddress?.taxExempt;
  } else {
    taxExemptFlag = transactionAccount?.billToParty?.taxExempt;
  }

  return (
    <SummarySection columns="2fr 1fr" className="invoice-group-summary">
      <SummaryGridItem className="summary-grid-item-internal">
        <SummarySection className="summary-section-internal" columns="1fr 1fr 1fr 1fr">
          <SummaryGridItem className="section-item-internal">
            <div className="sold-to-address">
              <label>Sold-to address</label>
              {shipToAddress?.addressLine1 ? (
                <div className="address-details">
                  <FormattedAddress
                    addressLine1={shipToAddress?.addressLine1}
                    addressLine2={shipToAddress?.addressLine2}
                    city={shipToAddress?.city}
                    country={shipToAddress?.country}
                    postcode={shipToAddress?.postcode}
                    state={shipToAddress?.state}
                    organizationName={shipToAddress?.name}
                  />
                </div>
              ) : (
                <p>Same as billing address</p>
              )}
            </div>
          </SummaryGridItem>
          {/* <SummaryGridItem className="section-item-internal">
            <TaxId taxId={shipToAddress?.taxId ?? 'No tax ID found'} />
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal">
            <TaxExempt taxExempt={shipToAddress?.taxExempt ? 'Yes' : 'No'} />
          </SummaryGridItem> */}
          <TaxComponent
            billToAddress={shipToAddress?.addressLine1 ? shipToAddress : transactionAccount?.billToParty}
            taxExemptFlag={taxExemptFlag ? 'Yes' : 'No'}
            transactionAccountNumber={transactionAccountNumber}
          />
          <SummaryGridItem className="section-item-internal">
            {/* <FeatureSwitch flag={ENABLE_PO_NUMBER_INVOICE_GROUP}> */}
            {/* <Enabled> */}
            <label>PO number</label>
            {purchaseOrder && purchaseOrder?.number ? (
              <>
                <div>{purchaseOrder.number}</div>
                <div>({purchaseOrder?.oneTimeUse ? 'Single-use' : 'Multi-use'})</div>
              </>
            ) : (
              <p>Not provided</p>
            )}
            {/* </Enabled> */}
            {/* </FeatureSwitch> */}
          </SummaryGridItem>
        </SummarySection>
        <SummarySection className="summary-section-internal" columns="1fr 1fr 1fr 1fr">
          <SummaryGridItem className="section-item-internal">
            <div>
              <label>Currency</label>
              <p>{currency ? currency : 'No currency found'}</p>
            </div>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal">
            <div>
              <label>Pricing eligibility</label>
              {shipToAddress?.shipToPriceEligibility ? (
                <div>{sentenceCase(shipToAddress.shipToPriceEligibility)}</div>
              ) : (
                <p>Commercial</p>
              )}
            </div>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal">
            <>
              <label>Invoice recipients</label>
              {isRecipientAvailable ? (
                <div className="recipient-section">
                  <div className="list">
                    {recipients && recipients[0]}
                    {recipients && recipients.length - 1 !== 0 && <p>{`+${recipients.length - 1} more`}</p>}
                  </div>
                </div>
              ) : (
                <p>No recipients</p>
              )}
              {isResourceAccess(Resources.INVOICE_GROUP_ALL) && isAccess(Permissions.INVOICE_GROUP_WRITE) && (
                <OpenSidebarButton
                  appearance="link"
                  spacing="none"
                  sidebar={SIDEBAR.UPDATE_INVOICE_RECIPIENTS}
                  sidebarOptions={{
                    sidebarWidth: '600px',
                    isOverlay: true,
                  }}
                  payload={{
                    transactionAccountId,
                    invoiceGroupId: id,
                    recipients: isRecipientAvailable ? recipients : [''],
                    shipToParty: shipToAddress,
                    refetch,
                  }}
                  testId="update-invoice-recipient"
                  label="Edit"
                >
                  Edit
                </OpenSidebarButton>
              )}
            </>
          </SummaryGridItem>
        </SummarySection>
      </SummaryGridItem>

      <SummaryGridItem className="summary-grid-item-internal payment-method-summary">
        <label>Payment method</label>
        {isPaymentIssue && <PaymentErrorLozenge />}
        {defaultPaymentMethodObj && Object.keys(defaultPaymentMethodObj)?.length ? (
          <div className="payment-method-container">
            {isEnableCreditManagement && defaultPaymentMethodObj?.type === PaymentMethodType.DEFERRED ? (
              <TermPaymentMethod
                paymentNetType={defaultPaymentMethodObj?.paymentMethod}
                totalLimit={defaultPaymentMethodObj?.totalCreditLimit}
                currentTermAccountStatus={termPaymentStatus?.status}
              />
            ) : (
              <PaymentMethodPanel
                paymentMethod={{
                  id: defaultPaymentMethodObj?.id as PaymentMethodId,
                  type: defaultPaymentMethodObj?.type,
                  // Because the 'card' prop should be used with PaymentMethodType.CARD type
                  ...(defaultPaymentMethodObj?.type === PaymentMethodType.CARD && {
                    card: {
                      brand: defaultPaymentMethodObj?.card?.brand ?? '',
                      expMonth,
                      expYear,
                      last4: defaultPaymentMethodObj?.card?.last4Numbers ?? '',
                      name: defaultPaymentMethodObj?.card?.cardHolderName ?? '',
                    },
                  }),
                  default: defaultPaymentMethodObj?.default ?? false,
                }}
              />
            )}
          </div>
        ) : (
          <p>No payment method found</p>
        )}
        {isResourceAccess(Resources.INVOICE_GROUP_ALL) && isAccess(Permissions.INVOICE_GROUP_WRITE) && (
          <Button
            appearance="link"
            onClick={() => {
              setConfirmDialogOpen(true);
            }}
            testId="manage-payment-methods-button"
            aria-label="Manage payment"
          >
            Manage payment
          </Button>
        )}
      </SummaryGridItem>
      <WarningDialog
        isOpen={isConfirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={() => {
          setConfirmDialogOpen(false);
          openSidebar({
            sidebarType: managePaymentMethodsSidebar,
            sidebarOptions: {
              sidebarWidth: '600px',
              isOverlay: true,
            },
            payload: preparePayloadForSidebar({
              transactionAccount: transactionAccount as TransactionAccount,
              defaultPaymentMethod: defaultPaymentMethod as Maybe<PaymentMethod>,
              refetch,
              invoiceGroupId: id as string,
              updatePaymentMethodLink: updatePaymentMethodLink as string,
              submitAction,
              isTermPaymentMethodAllowed,
              termPaymentStatus: termPaymentStatus?.status,
            }),
          });
        }}
        header={'Other Subscriptions will also be affected'}
        description={
          <div>
            <p>This action will also change the payment method for other subscriptions in this billing profile.</p>
            <br />
            <p>You can view the linked subscriptions on the next screen.</p>
          </div>
        }
        okLabel={'Ok,got it'}
        testId={'manage-payment-warning-dialog'}
      />
    </SummarySection>
  );
};
